import React, { useState } from "react";
import {
  Flex,
  FormLabel,
  HStack,
  Text,
  FormControl,
  Input,
} from "@chakra-ui/react";
import ChakraPopUp from "../../common/PopUp/ChakraPopUp";
import { fetchWithToken } from "../../../functions/fetch";
import { useQuery } from "@tanstack/react-query";
import uniqid from "uniqid";
import { useFormik } from "formik";
import FormikSelect from "../../common/PopUp/ChakraFormik/FormikSelect";
import { postReservationSchema } from "../../../validation/reservationValidation";

const formatTimeOption = (hour) => {
  const hourInt = Math.floor(hour);
  const hourStr = String(hourInt).padStart(2, "0");
  const minStr = String((hour - hourInt) * 60).padStart(2, "0");
  return (
    <option key={uniqid()} value={String(hour)}>
      {hourStr}:{minStr}
    </option>
  );
};

const NewReservationPopUp = ({ isOpen, onClose, setRefetchReservation }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);

  const purposeOptions = ["看房", "入住簽約"]; // index of the option is value sent to backend
  const timeOptions = [
    8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15, 15.5,
    16, 16.5, 17,
  ];

  const { data: users } = useQuery({
    queryKey: ["user"],
    queryFn: async () =>
      (
        await fetchWithToken(
          "GET",
          `${process.env.REACT_APP_BACKEND_HOST}/api/user`,
        )
      ).users,
    initialData: [],
  });

  const { data: reservableRooms } = useQuery({
    queryKey: ["room", "reservable"],
    queryFn: async () =>
      (
        await fetchWithToken(
          "GET",
          `${process.env.REACT_APP_BACKEND_HOST}/api/room/reservable`,
        )
      ).rooms,
    initialData: [],
  });

  const getErrors = (formik) => {
    const { values, touched, errors } = formik;
    const arr = [];
    for (const prop in values) {
      if (touched[prop] && errors[prop]) {
        arr.push(errors[prop]);
      }
    }
    return arr.map((item) => (
      <Text color="red.500" key={uniqid()}>
        {item}
      </Text>
    ));
  };

  const formik = useFormik({
    initialValues: {
      lineId: "",
      purpose: "0",
      roomNumber: "",
      date: "",
      hour: "",
    },
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      const { date, hour, lineId, purpose, roomNumber } = values;
      const startTime = `${date}T${String(Math.floor(hour)).padStart(2, "0")}:${String(60 * (hour - Math.floor(hour))).padStart(2, "0")}+08:00`;
      const body = {
        lineId,
        purpose,
        startTime,
        roomNumber,
        userNote: "",
      };
      console.table(body);

      const res = await fetchWithToken(
        "POST",
        `${process.env.REACT_APP_BACKEND_HOST}/api/reservation`,
        body,
      );
      if (res.status === 200) {
        // refetch
        setRefetchReservation(true);
        resetForm(); // clear input data
        onClose();
      } else {
        console.error(res);
        setErrors(res.errors);
      }
      setIsSubmitting(false);
    },
    validationSchema: postReservationSchema,
    enableReinitialize: true,
  });

  return (
    <ChakraPopUp
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      size="xl"
      title="新增預約"
    >
      <Flex direction="column" gap={2}>
        <HStack>
          <FormikSelect
            labelText="姓名"
            formik={formik}
            field="lineId"
            isRequired
          >
            <option value="">無</option>
            {users?.map((user) => (
              <option key={uniqid()} value={user.lineId}>
                {user.name}
              </option>
            ))}
          </FormikSelect>
          <HStack w="full">
            <FormLabel whiteSpace="nowrap" mb={0}>
              連絡電話
            </FormLabel>
            <FormLabel whiteSpace="nowrap" mb={0}>
              {users.find((user) => user.lineId === formik.values.lineId)
                ?.phoneNumber ?? "無"}
            </FormLabel>
          </HStack>
        </HStack>
        <HStack>
          <FormikSelect
            labelText="目的"
            formik={formik}
            field="purpose"
            isRequired
          >
            {purposeOptions.map((text, index) => (
              <option key={uniqid()} value={String(index)}>
                {text}
              </option>
            ))}
          </FormikSelect>
          <FormikSelect
            labelText="預定房號"
            formik={formik}
            field="roomNumber"
            isRequired
          >
            <option value="">無</option>
            {reservableRooms.map((room) => (
              <option key={uniqid()} value={room.roomNumber}>
                {room.roomNumber}
              </option>
            ))}
          </FormikSelect>
        </HStack>
        <HStack>
          <FormControl isRequired>
            <HStack>
              <FormLabel whiteSpace="nowrap" mb={0}>
                日期
              </FormLabel>
              <Input
                type="date"
                variant="filled"
                {...formik.getFieldProps("date")}
                w="fit-content"
              />
            </HStack>
          </FormControl>
          <FormikSelect
            labelText="時間"
            formik={formik}
            field="hour"
            isRequired
          >
            <option value="">無</option>
            {timeOptions.map((hour) => formatTimeOption(hour))}
          </FormikSelect>
        </HStack>
        {errors?.map((err) => (
          <Text color="red.500" key={uniqid()}>
            {err.msg}
          </Text>
        ))}
        {getErrors(formik)}
      </Flex>
    </ChakraPopUp>
  );
};

export default NewReservationPopUp;
